import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import classNames from 'classnames';

import appSettings from '../../config/settings';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';

import {
  Button,
  Form,
  FieldCurrencyInput,
  FieldTextInput,
  InlineTextButton,
} from '../../components';

import css from './EditListingAddOnsForm.module.css';

const { Money } = sdkTypes;

const getPriceValidators = (listingMinimumPriceSubUnits, marketplaceCurrency, intl) => {
  const priceRequired = validators.required(
    intl.formatMessage({ id: 'EditListingAddOnsForm.priceRequired' })
  );

  if (!listingMinimumPriceSubUnits) {
    return priceRequired;
  }

  const minPriceRaw = new Money(listingMinimumPriceSubUnits, marketplaceCurrency);
  const minPrice = formatMoney(intl, minPriceRaw);
  const minPriceRequired = validators.moneySubUnitAmountAtLeast(
    intl.formatMessage({ id: 'EditListingAddOnsForm.priceTooLow' }, { minPrice }),
    listingMinimumPriceSubUnits
  );

  return validators.composeValidators(priceRequired, minPriceRequired);
};

const EditListingAddOnsForm = props => {
  const intl = useIntl();

  return (
    <FinalForm
      {...props}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          formId = 'EditListingAddOnsForm',
          className,
          disabled,
          ready,
          handleSubmit,
          marketplaceCurrency,
          unitType,
          listingMinimumPriceSubUnits = 0,
          invalid,
          pristine,
          saveActionMsg,
          updated,
          updateInProgress,
          fetchErrors,
        } = formRenderProps;

        const priceValidators = getPriceValidators(
          listingMinimumPriceSubUnits,
          marketplaceCurrency,
          intl
        );

        const classes = classNames(css.root, className);
        const submitReady = (updated && pristine) || ready;
        const submitInProgress = updateInProgress;
        const submitDisabled = invalid || disabled || submitInProgress;
        const { updateListingError, showListingsError } = fetchErrors || {};

        return (
          <Form onSubmit={handleSubmit} className={classes}>
            {updateListingError ? (
              <p className={css.error}>
                <FormattedMessage id="EditListingAddOnsForm.updateFailed" />
              </p>
            ) : null}
            {showListingsError ? (
              <p className={css.error}>
                <FormattedMessage id="EditListingAddOnsForm.showListingFailed" />
              </p>
            ) : null}

            <FieldArray name="addOns">
              {({ fields }) => (
                <div className={css.addOns}>
                  {fields.map((name, index) => (
                    <div className={css.addOn} key={name}>
                      <FieldTextInput
                        type="text"
                        id={`${formId}.addOns[${index}].name`}
                        name={`${name}.name`}
                        className={css.input}
                        label={intl.formatMessage({ id: 'EditListingAddOnsForm.addOnName' })}
                        placeholder={intl.formatMessage({
                          id: 'EditListingAddOnsForm.addOnNamePlaceholder',
                        })}
                        validate={validators.required(
                          intl.formatMessage({ id: 'EditListingAddOnsForm.addOnNameRequired' })
                        )}
                      />
                      <FieldCurrencyInput
                        id={`${formId}.addOns[${index}].price`}
                        name={`${name}.price`}
                        className={css.input}
                        label={intl.formatMessage({ id: 'EditListingAddOnsForm.addOnPrice' })}
                        placeholder={intl.formatMessage({
                          id: 'EditListingAddOnsForm.addOnPricePlaceholder',
                        })}
                        currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                        validate={priceValidators}
                      />
                    </div>
                  ))}

                  <InlineTextButton
                    type="button"
                    className={css.buttonAddNew}
                    onClick={() => fields.push({ name: '', price: null })}
                  >
                    <FormattedMessage id="EditListingAddOnsForm.addAnother" />
                  </InlineTextButton>
                </div>
              )}
            </FieldArray>

            <Button
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
              ready={submitReady}
            >
              {saveActionMsg}
            </Button>
          </Form>
        );
      }}
    />
  );
};

export default EditListingAddOnsForm;
